import React, { useState } from 'react';

const UsersTable = ({ users, onEdit, onEditEmail, onDelete, currentUserId }) => {
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-600';
      case 'artist':
        return 'bg-purple-600';
      default:
        return 'bg-gray-600';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter users by role then by search
  const filteredUsers = users
    .filter(user => roleFilter === 'all' || user.role === roleFilter)
    .filter(user => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (user.email || '').toLowerCase().includes(q) ||
        (user.username || '').toLowerCase().includes(q)
      );
    });

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      {/* Filter Bar */}
      <div className="p-4 bg-gray-750 border-b border-gray-700 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <input
            type="text"
            placeholder="Search by email or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64 px-4 py-2 bg-battle-dark border border-army/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-yellow/60 transition-colors text-sm"
            data-testid="users-search-input"
          />
          <div className="flex items-center space-x-2">
            <label htmlFor="role-filter" className="text-sm font-medium text-gray-300">
              Role:
            </label>
            <select
              id="role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
            >
              <option value="all">All Users ({users.length})</option>
              <option value="admin">Admins ({users.filter(u => u.role === 'admin').length})</option>
              <option value="artist">Artists ({users.filter(u => u.role === 'artist').length})</option>
              <option value="viewer">Viewers ({users.filter(u => u.role === 'viewer').length})</option>
            </select>
          </div>
        </div>
        <div className="text-sm text-gray-400">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Username
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                  {roleFilter === 'all' ? 'No users found' : `No ${roleFilter}s found`}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-750">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-white">
                          {user.username}
                          {user.id === currentUserId && (
                            <span className="ml-2 text-xs text-gray-400">(You)</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-400">{user.display_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">
                      {user.email}
                      {user.email.endsWith('@verbalwarzone.com') && (
                        <span className="ml-2 text-xs text-yellow-400">(Auto-generated)</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)} text-white`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => onEdit(user)}
                      className="text-blue-400 hover:text-blue-300 mr-3"
                    >
                      Edit Role
                    </button>
                    <button
                      onClick={() => onEditEmail(user)}
                      className="text-green-400 hover:text-green-300 mr-3"
                    >
                      Edit Email
                    </button>
                    {user.id !== currentUserId && (
                      <button
                        onClick={() => onDelete(user.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersTable;
